import { createTheme } from "@mui/material/styles";

// ─── olivv.ai exact design tokens ───────────────────────────────────────────
const FOREST      = "#053418"; // --color-forest       (primary dark, headings, nav)
const FOREST_600  = "#0a2818"; // --color-forest-600
const ACCENT      = "#00c853"; // --color-accent        (CTA buttons, active states)
const ACCENT_HOVER= "#00a847"; // --color-accent-hover
const ACCENT_SOFT = "#d6f1d8"; // --color-accent-soft   (hover fills, chips, badges)
const INK         = "#1a1a1a"; // --color-ink           (body text)
const MUTED       = "#6b7368"; // --color-muted
const MUTED_2     = "#9aa39a"; // --color-muted-2
const LINE        = "#e6e8df"; // --color-line          (borders, dividers)
const CREAM       = "#fafaf7"; // --color-cream         (page background)
const CREAM_50    = "#fcfcfa"; // --color-cream-50      (input backgrounds)
const CREAM_200   = "#e5ead9"; // --color-cream-200
const WHITE       = "#ffffff";

const SHADOW_CARD = "0 1px 0 0 rgba(10,40,24,0.04), 0 12px 32px -16px rgba(10,40,24,0.18)";
const SHADOW_SOFT = "0 1px 0 0 rgba(10,40,24,0.04), 0 4px 16px -8px rgba(10,40,24,0.10)";

const muiTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main:         ACCENT,       // olivv CTA green — used for contained buttons, focus rings, switches
      light:        ACCENT_SOFT,
      dark:         ACCENT_HOVER,
      contrastText: FOREST,       // dark text on bright green button
    },
    secondary: {
      main:         FOREST,       // olivv forest — used for dark headings/nav tones
      light:        ACCENT_SOFT,
      dark:         FOREST_600,
      contrastText: WHITE,
    },
    success: { main: ACCENT,   contrastText: FOREST },
    error:   { main: "#c0392b", contrastText: WHITE },
    warning: { main: "#d97706", contrastText: WHITE },
    info:    { main: "#0284c7", contrastText: WHITE },
    background: {
      default: CREAM,   // --color-cream: whole page background
      paper:   WHITE,   // surfaces / cards
    },
    text: {
      primary:   INK,    // --color-ink
      secondary: MUTED,  // --color-muted
      disabled:  MUTED_2,
    },
    divider: LINE,       // --color-line
    action: {
      hover:    ACCENT_SOFT,
      selected: ACCENT_SOFT,
      focus:    ACCENT_SOFT,
    },
  },

  shape: { borderRadius: 10 },

  typography: {
    fontFamily: "GilroyRegular, ui-sans-serif, system-ui, sans-serif",
    h1: { fontFamily: "GilroyBold",     color: FOREST },
    h2: { fontFamily: "GilroyBold",     color: FOREST },
    h3: { fontFamily: "GilroyBold",     color: FOREST },
    h4: { fontFamily: "GilroyBold",     color: FOREST },
    h5: { fontFamily: "GilroyBold",     color: FOREST },
    h6: { fontFamily: "GilroyBold",     color: FOREST },
    subtitle1: { fontFamily: "GilroySemiBold" },
    subtitle2: { fontFamily: "GilroySemiBold" },
    body1:     { fontFamily: "GilroyRegular", color: INK },
    body2:     { fontFamily: "GilroyRegular", color: MUTED },
    button:    { fontFamily: "GilroySemiBold", textTransform: "none" },
    caption:   { fontFamily: "GilroyRegular", color: MUTED },
  },

  components: {
    // ── Buttons ───────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontFamily: "GilroySemiBold",
          paddingTop: 9,
          paddingBottom: 9,
          paddingLeft: 20,
          paddingRight: 20,
          fontSize: 14,
        },
        containedPrimary: {
          backgroundColor: ACCENT,
          color: FOREST,
          "&:hover": { backgroundColor: ACCENT_HOVER },
        },
        containedSecondary: {
          backgroundColor: FOREST,
          color: WHITE,
          "&:hover": { backgroundColor: FOREST_600 },
        },
        outlinedPrimary: {
          borderColor: LINE,
          color: INK,
          "&:hover": { borderColor: MUTED_2, backgroundColor: CREAM },
        },
        text: {
          color: MUTED,
          "&:hover": { backgroundColor: ACCENT_SOFT, color: FOREST },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: MUTED,
          "&:hover": { backgroundColor: ACCENT_SOFT, color: FOREST },
        },
      },
    },

    // ── App bar (Navbar) ──────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: { color: "inherit", elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: CREAM,
          color: INK,
          boxShadow: "none",
          borderBottom: `1px solid ${LINE}`,
        },
      },
    },

    // ── Drawer (Sidebar) ──────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: WHITE,
          borderRight: `1px solid ${LINE}`,
        },
      },
    },

    // ── Cards & surfaces ──────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: { borderRadius: 12 },
        elevation1: { boxShadow: SHADOW_SOFT },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${LINE}`,
          boxShadow: SHADOW_CARD,
          backgroundColor: WHITE,
        },
      },
    },

    // ── Dialog ────────────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: SHADOW_CARD,
          border: `1px solid ${LINE}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: "GilroyBold", fontSize: 16, color: FOREST, paddingBottom: 8 },
      },
    },

    // ── Inputs ────────────────────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: CREAM_50,
          fontFamily: "GilroyRegular",
          fontSize: 14,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: LINE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: MUTED_2 },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: FOREST,
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: CREAM_50,
          borderRadius: "10px 10px 0 0",
          fontFamily: "GilroyRegular",
          fontSize: 14,
          "&:hover": { backgroundColor: CREAM_200 },
          "&.Mui-focused": { backgroundColor: CREAM_50 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: "GilroyRegular",
          fontSize: 13.5,
          color: MUTED,
          "&.Mui-focused": { color: FOREST },
        },
      },
    },

    // ── Select ────────────────────────────────────────────────────────────
    MuiSelect: {
      styleOverrides: {
        icon: { color: MUTED },
      },
    },

    // ── List items (sidebar nav, menus) ───────────────────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: "GilroySemiBold",
          "&.Mui-selected": {
            backgroundColor: ACCENT_SOFT,
            color: FOREST,
            "&:hover": { backgroundColor: ACCENT_SOFT },
          },
          "&:hover": { backgroundColor: "#f0f5ee" },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: "GilroyRegular",
          fontSize: 13.5,
          "&.Mui-selected": { backgroundColor: ACCENT_SOFT },
          "&:hover": { backgroundColor: ACCENT_SOFT },
        },
      },
    },

    // ── Table ─────────────────────────────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: "GilroySemiBold",
          fontSize: 12,
          color: MUTED,
          backgroundColor: CREAM_50,
          borderBottom: `1px solid ${LINE}`,
        },
        body: {
          fontFamily: "GilroyRegular",
          fontSize: 13,
          color: INK,
          borderBottom: `1px solid ${LINE}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: CREAM },
          "&:last-child td": { border: 0 },
        },
      },
    },

    // ── Chip ──────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          fontFamily: "GilroySemiBold",
          fontSize: 12,
          height: 24,
        },
        colorPrimary: {
          backgroundColor: ACCENT_SOFT,
          color: FOREST,
        },
      },
    },

    // ── Switch ────────────────────────────────────────────────────────────
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        thumb: { boxShadow: "none" },
        track: { borderRadius: 12, opacity: 0.25 },
        switchBase: {
          "&.Mui-checked": {
            color: ACCENT,
            "& + .MuiSwitch-track": { backgroundColor: ACCENT, opacity: 0.5 },
          },
        },
      },
    },

    // ── Checkbox & Radio ──────────────────────────────────────────────────
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: MUTED_2,
          "&.Mui-checked": { color: ACCENT },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: MUTED_2,
          "&.Mui-checked": { color: ACCENT },
        },
      },
    },

    // ── Tabs ──────────────────────────────────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "GilroySemiBold",
          fontSize: 13.5,
          textTransform: "none",
          color: MUTED,
          minHeight: 44,
          "&.Mui-selected": { color: FOREST },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: ACCENT },
      },
    },

    // ── Tooltip ───────────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 7,
          fontSize: 12,
          fontFamily: "GilroyRegular",
          backgroundColor: FOREST,
          color: WHITE,
        },
        arrow: { color: FOREST },
      },
    },

    // ── Popover & Menu ────────────────────────────────────────────────────
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${LINE}`,
          boxShadow: SHADOW_CARD,
        },
      },
    },

    // ── Breadcrumbs ───────────────────────────────────────────────────────
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { color: MUTED_2 },
      },
    },

    // ── Divider ───────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: LINE },
      },
    },

    // ── Circular progress ─────────────────────────────────────────────────
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: { color: ACCENT },
      },
    },

    // ── Scrollbar (webkit) ────────────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: `
        body { background-color: ${CREAM}; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${CREAM}; }
        ::-webkit-scrollbar-thumb { background: ${CREAM_200}; border-radius: 8px; }
        ::-webkit-scrollbar-thumb:hover { background: ${MUTED_2}; }
      `,
    },
  },
});

export default muiTheme;
